import {
  useCreateCoursePlan,
  useDeleteCoursePlan,
  useGetAllCoursesLite,
  useGetCoursePlan,
  useListCoursePlans,
} from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import Textarea from '@components/V4/Inputs/Textarea';
import Select from '@components/V4/Select/Select';
import {
  AnnotationIcon,
  CheckCircleIcon,
  DownloadIcon,
  ExclamationIcon,
  LightBulbIcon,
  RefreshIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import { EmptyState, FilterField, SectionHeading } from '@modules/Admin/Reporting/common';
import { CoursePlanStatus, CoursePlanSummaryDTO } from '@shared/apis/coursePlans.apis';
import useToast from 'apps/agora/src/hooks/useToast';
import { mergeClassNames } from 'apps/agora/src/utils/helpers';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as ROUTES from '../../../constants/routes';
import PlanView, { SourcesPanel, planToMarkdown } from './PlanView';

const WINDOW_OPTIONS = [
  { label: 'Last 6 months of sessions', value: 6 },
  { label: 'Last 12 months of sessions', value: 12 },
  { label: 'Last 18 months of sessions', value: 18 },
  { label: 'Last 24 months of sessions', value: 24 },
  { label: 'Last 36 months of sessions', value: 36 },
];

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Romanian', value: 'ro' },
];

const STATUS_STYLES: Record<CoursePlanStatus, string> = {
  queued: 'bg-white/10 text-customGrey',
  running: 'bg-customGold/15 text-customGold',
  completed: 'bg-customGreen/15 text-customGreen',
  failed: 'bg-customRed/15 text-customRed',
};

const download = (fileName: string, content: string, type: string) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
};

const RunRow = (props: {
  run: CoursePlanSummaryDTO;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) => {
  const { run, isSelected, onSelect, onDelete } = props;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event) => (event.key === 'Enter' ? onSelect() : undefined)}
      className={mergeClassNames(
        'flex cursor-pointer items-start justify-between gap-3 rounded-xl px-4 py-3 transition-colors',
        isSelected ? 'bg-white/10' : 'hover:bg-white/5'
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <span className="truncate text-sm font-semibold text-white">
          {run.planTitle ?? run.course.title}
        </span>
        <span className="text-xs text-customGrey">
          {moment(run.createdAt).format('D MMM YYYY HH:mm')}
          {run.createdBy?.fullName ? ` · ${run.createdBy.fullName}` : ''}
          {run.durationMs ? ` · ${Math.round(run.durationMs / 1000)}s` : ''}
          {' · '}
          {run.options.language === 'ro' ? 'RO' : 'EN'} · {run.options.windowMonths} mo
        </span>
        <span
          className={mergeClassNames(
            'w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
            STATUS_STYLES[run.status]
          )}
        >
          {run.status === 'running' ? run.stage ?? 'running' : run.status}
        </span>
      </div>
      {run.status !== 'running' ? (
        <button
          type="button"
          className="shrink-0 text-customGrey hover:text-customRed"
          title="Delete this run"
          onClick={(event) => {
            event.stopPropagation();
            onDelete();
          }}
        >
          <TrashIcon className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
};

const CoursePlanGenerator = () => {
  const toast = useToast();
  const history = useHistory();
  const [courseId, setCourseId] = useState<string>();
  const [windowMonths, setWindowMonths] = useState(18);
  const [language, setLanguage] = useState<'en' | 'ro'>('en');
  const [focus, setFocus] = useState('');
  const [selectedId, setSelectedId] = useState<string>();

  const { data: courses, isLoading: isLoadingCourses } = useGetAllCoursesLite();

  const {
    data: runs,
    isLoading: isLoadingRuns,
    refetch: refetchRuns,
  } = useListCoursePlans({ courseId }, { enabled: !!courseId, refetchOnWindowFocus: false });

  const hasRunning = (runs ?? []).some(
    (run) => run.status === 'running' || run.status === 'queued'
  );

  const { data: selected, refetch: refetchSelected } = useGetCoursePlan(selectedId ?? '', {
    enabled: !!selectedId,
    refetchOnWindowFocus: false,
  });

  const selectedIsLive = selected?.status === 'running' || selected?.status === 'queued';

  useEffect(() => {
    if (!hasRunning && !selectedIsLive) return;
    const timer = setInterval(() => {
      refetchRuns();
      if (selectedId) refetchSelected();
    }, 4000);
    return () => clearInterval(timer);
  }, [hasRunning, selectedIsLive, selectedId, refetchRuns, refetchSelected]);

  useEffect(() => {
    if (!runs?.length) {
      setSelectedId(undefined);
      return;
    }
    if (!selectedId || !runs.some((run) => run._id === selectedId)) setSelectedId(runs[0]._id);
  }, [runs, selectedId]);

  const { mutate: createPlan, isLoading: isCreating } = useCreateCoursePlan({
    onSuccess: (plan) => {
      toast.success('Generation started. This usually takes a few minutes.');
      setSelectedId(plan._id);
      refetchRuns();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.error?.message ?? 'Could not start the generation.');
    },
  });

  const { mutate: deletePlan } = useDeleteCoursePlan(selectedId ?? '', {
    onSuccess: () => {
      toast.success('Run deleted.');
      setSelectedId(undefined);
      refetchRuns();
    },
    onError: () => toast.error('Could not delete the run.'),
  });

  const courseOptions = useMemo(
    () =>
      [...(courses ?? [])]
        .sort((a, b) => a.title.localeCompare(b.title))
        .map((course) => ({
          value: course._id,
          label: course.abbreviation ? `${course.title} (${course.abbreviation})` : course.title,
        })),
    [courses]
  );

  const selectedCourse = courses?.find((course) => course._id === courseId);

  return (
    <div className="flex flex-col gap-8 p-8 font-raleway text-white">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold">Course Plan Generator</h1>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl bg-surfaceObject p-5">
        <SectionHeading title="Generate a plan" />
        <div className="flex flex-wrap items-end gap-4">
          <FilterField width="w-full laptop:w-96">
            <Select
              label="Course"
              variant="secondary"
              showSearch
              isLoading={isLoadingCourses}
              value={courseId}
              options={courseOptions}
              onSelect={(value) => {
                setCourseId(value ? String(value) : undefined);
                setSelectedId(undefined);
              }}
              placeholder="Pick a course"
            />
          </FilterField>
          <FilterField width="w-full tablet:w-64">
            <Select
              label="Sessions to analyse"
              variant="secondary"
              value={windowMonths}
              options={WINDOW_OPTIONS}
              onSelect={(value) => setWindowMonths(Number(value))}
            />
          </FilterField>
          <FilterField width="w-full tablet:w-44">
            <Select
              label="Plan language"
              variant="secondary"
              value={language}
              options={LANGUAGE_OPTIONS}
              onSelect={(value) => setLanguage(value === 'ro' ? 'ro' : 'en')}
            />
          </FilterField>
        </div>
        <Textarea
          id="course-plan-focus"
          name="focus"
          label="Focus (optional)"
          placeholder="e.g. Rebuild as a 12-session course for 2027 applicants, emphasise interview preparation, keep it in line with the new UCAS personal statement format."
          rows={2}
          value={focus}
          onChange={(event) => setFocus(event.target.value)}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="text-xs text-customGrey">
            Reads up to 120 transcribed sessions. Each run costs OpenAI credits and takes a few
            minutes; one run per course at a time.
          </span>
          <Button
            buttonText={isCreating ? 'Starting…' : 'Generate plan'}
            icon={<LightBulbIcon className="h-4 w-4" />}
            disabled={!courseId || isCreating || hasRunning}
            isLoading={isCreating}
            onClick={() =>
              courseId &&
              createPlan({
                courseId,
                options: { windowMonths, language, focus: focus || undefined },
              })
            }
          />
        </div>
      </div>

      {!courseId ? (
        <EmptyState>Pick a course to see previous plans or generate a new one.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-6 laptop:grid-cols-[320px_1fr]">
          <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-3">
            <div className="flex items-center justify-between px-2 pt-1">
              <span className="text-sm font-semibold text-white">
                Runs for {selectedCourse?.title ?? 'this course'}
              </span>
              <button
                type="button"
                className="text-customGrey hover:text-white"
                title="Refresh"
                onClick={() => refetchRuns()}
              >
                <RefreshIcon className="h-4 w-4" />
              </button>
            </div>
            {isLoadingRuns ? (
              <div className="flex h-24 items-center justify-center">
                <LoadingSpinner size="normal" />
              </div>
            ) : !runs?.length ? (
              <span className="px-2 pb-2 text-sm text-customGrey">No plans generated yet.</span>
            ) : (
              runs.map((run) => (
                <RunRow
                  key={run._id}
                  run={run}
                  isSelected={run._id === selectedId}
                  onSelect={() => setSelectedId(run._id)}
                  onDelete={() => {
                    setSelectedId(run._id);
                    deletePlan({});
                  }}
                />
              ))
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-6">
            {!selected ? (
              <EmptyState>Select a run to see its plan.</EmptyState>
            ) : selected.status === 'failed' ? (
              <div className="flex items-start gap-3 rounded-2xl bg-customRed/10 p-5 text-sm text-customRed">
                <ExclamationIcon className="mt-0.5 h-5 w-5 shrink-0" />
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">This run failed.</span>
                  <span>{selected.error}</span>
                </div>
              </div>
            ) : selected.status !== 'completed' ? (
              <div className="flex flex-col gap-3 rounded-2xl bg-surfaceObject p-5">
                <div className="flex items-center gap-3 text-sm text-white">
                  <LoadingSpinner size="normal" />
                  <span className="font-semibold">{selected.stage ?? 'Starting…'}</span>
                </div>
                {selected.progress ? (
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/10">
                    <div
                      className="h-full rounded-full bg-customGold"
                      style={{
                        width: `${Math.round(
                          (selected.progress.done / Math.max(selected.progress.total, 1)) * 100
                        )}%`,
                      }}
                    />
                  </div>
                ) : null}
                <span className="text-xs text-customGrey">
                  Collecting content, reading transcripts in batches, researching online, then
                  writing the plan. The page refreshes on its own.
                </span>
              </div>
            ) : selected.plan ? (
              <>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span className="inline-flex items-center gap-2 text-sm text-customGreen">
                    <CheckCircleIcon className="h-5 w-5" />
                    Generated {moment(selected.finishedAt).fromNow()}
                    {selected.model ? ` with ${selected.model}` : ''}
                  </span>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="secondary"
                      buttonText={`Open review (v${selected.version}${
                        selected.openCommentCount ? `, ${selected.openCommentCount} open` : ''
                      })`}
                      icon={<AnnotationIcon className="h-4 w-4" />}
                      onClick={() => history.push(`${ROUTES.COURSE_PLANS}?plan=${selected._id}`)}
                    />
                    <Button
                      variant="secondary"
                      buttonText="Download Markdown"
                      icon={<DownloadIcon className="h-4 w-4" />}
                      onClick={() =>
                        selected.plan &&
                        download(
                          `${selected.course.title.replace(/[^\w]+/g, '-')}-plan-${moment(
                            selected.finishedAt
                          ).format('YYYY-MM-DD')}.md`,
                          planToMarkdown(selected.plan),
                          'text/markdown'
                        )
                      }
                    />
                    <Button
                      variant="secondary"
                      buttonText="Download JSON"
                      icon={<DownloadIcon className="h-4 w-4" />}
                      onClick={() =>
                        download(
                          `${selected.course.title.replace(/[^\w]+/g, '-')}-plan-${moment(
                            selected.finishedAt
                          ).format('YYYY-MM-DD')}.json`,
                          JSON.stringify(selected, null, 2),
                          'application/json'
                        )
                      }
                    />
                  </div>
                </div>
                <SourcesPanel plan={selected} />
                <PlanView plan={selected.plan} research={selected.research?.markdown} />
              </>
            ) : (
              <EmptyState>This run finished without a plan.</EmptyState>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursePlanGenerator;
